import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import NoSqlAttributesEditor from "./nosql-attributes-editor";
import {
  NoSqlOntologyCollection,
  useCreateNoSqlCollection,
  useUpdateNoSqlCollection,
} from "@/service-hooks/use-get-nosql-ontology-service";

interface NoSqlCollectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  // When supplied the dialog is in edit mode.
  existing?: NoSqlOntologyCollection;
  onSaved?: () => void;
}

interface FormState {
  name: string;
  description: string;
  source: string;
  attributes: string;
}

const EMPTY: FormState = { name: "", description: "", source: "", attributes: "" };

export default function NoSqlCollectionDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  existing,
  onSaved,
}: NoSqlCollectionDialogProps) {
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const createCollection = useCreateNoSqlCollection();
  const updateCollection = useUpdateNoSqlCollection();
  const isEdit = !!existing?.id;
  const isPending = createCollection.isPending || updateCollection.isPending;

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      name: existing?.name ?? "",
      description: existing?.description ?? "",
      source: existing?.source ?? "",
      attributes: existing?.attributes ?? "",
    });
  }, [isOpen, existing]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    try {
      if (isEdit && existing?.id) {
        await updateCollection.mutateAsync({
          id: existing.id,
          projectUuid,
          dataLakeId,
          data: {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            source: form.source.trim() || undefined,
            attributes: form.attributes || undefined,
          },
        });
      } else {
        await createCollection.mutateAsync({
          projectUuid,
          dataLakeId,
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          source: form.source.trim() || undefined,
          attributes: form.attributes || undefined,
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Collection" : "Create Collection"}</DialogTitle>
          <DialogDescription className="sr-only">
            Configure the fields for this NoSQL collection.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="e.g. users, Patient (FHIR resource), UsersTable"
              className="bg-surface text-white"
            />
            {isEdit && (
              <p className="text-xs text-muted">
                Renaming the collection changes the MERGE key used by future
                ingests.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Source</label>
            <Input
              value={form.source}
              onChange={(e) => update("source", e.target.value)}
              placeholder="Engine hint — e.g. mongodb, dynamodb, redis, fhir"
              className="bg-surface text-white"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Description</label>
            <Textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="What this collection holds (optional)"
              className="bg-surface text-white min-h-[72px]"
            />
          </div>

          <NoSqlAttributesEditor
            value={form.attributes}
            onChange={(json) => update("attributes", json)}
            disabled={isPending}
          />
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.name.trim() || isPending}
            className="bg-primary hover:bg-primary-dark text-white"
          >
            {isPending ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
